import React from 'react';
import { Link } from 'react-router-dom';
import { Reveal } from '../components/ui';
import { Activity, AlertTriangle, CheckCircle, RefreshCw } from 'lucide-react';
import { useStatus } from '../context/StatusContext';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/Button';
import type { StatusBanner } from '../api/status';

/** Status banner severity → Alert variant (FairCorp palette). */
const bannerVariant: Record<StatusBanner['severity'], 'info' | 'warning' | 'destructive'> = {
  info: 'info',
  warning: 'warning',
  error: 'destructive',
};

/** Public status page: banners, service list, outage info. Catalog FC-089. */
const Status: React.FC = () => {
  const {
    banners,
    services,
    outagePageActive,
    outageInfo,
    publicHubStatusEnabled,
    loading,
    statusError,
    retryStatus,
  } = useStatus();

  if (!publicHubStatusEnabled) {
    return (
      <section className="page-shell page-section-major">
        <div className="page-inner">
        <Card className="shadow-none">
          <CardContent className="p-8 sm:p-10">
            <h1 className="text-2xl sm:text-3xl font-bold text-primary-300 mb-4">System Status</h1>
            <Alert variant="success">
              <CheckCircle className="size-4" />
              <AlertTitle>No public incidents at this time.</AlertTitle>
            </Alert>
            <Separator className="my-6" />
            <Link to="/" className="inline-flex items-center gap-2 text-primary-400 hover:text-primary-300 font-medium">
              Back to Home
            </Link>
          </CardContent>
        </Card>
        </div>
      </section>
    );
  }

  return (
    <section className="page-shell page-section-major">
        <div className="page-inner">
      <Reveal dir="up">
        <Card className="shadow-none overflow-hidden">
          <CardContent className="p-8 sm:p-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="size-12 rounded-full bg-primary-500/15 border border-primary-500/30 flex items-center justify-center">
                <Activity className="size-6 text-primary-400" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-primary-300">System Status</h1>
            </div>
            <p className="text-secondary-400 mb-6 text-lg">
              Current status of FairCorp services and any active notifications.
            </p>

            {loading ? (
              <div className="flex flex-col gap-3" aria-busy="true">
                <span className="sr-only">Loading status…</span>
                <Skeleton className="h-16 w-full rounded-xl" />
                <Skeleton className="h-11 w-full rounded-lg" />
                <Skeleton className="h-11 w-full rounded-lg" />
              </div>
            ) : statusError ? (
              <Alert variant="destructive">
                <AlertTriangle className="size-4" />
                <AlertTitle>Couldn&rsquo;t load status</AlertTitle>
                <AlertDescription>
                  <p className="mb-3">{statusError}</p>
                  <Button type="button" size="sm" onClick={retryStatus}>
                    <RefreshCw className="size-4" aria-hidden />
                    Retry
                  </Button>
                </AlertDescription>
              </Alert>
            ) : (
              <div className="flex flex-col gap-6">
                {outagePageActive && outageInfo && (
                  <Alert variant="warning">
                    <AlertTriangle className="size-4" />
                    <AlertTitle>Active Outage</AlertTitle>
                    <AlertDescription>
                      {outageInfo.expectedReturn && (
                        <p>Expected return: {outageInfo.expectedReturn}</p>
                      )}
                      {outageInfo.servicesImpacted && outageInfo.servicesImpacted.length > 0 && (
                        <p>Impacted: {outageInfo.servicesImpacted.join(', ')}</p>
                      )}
                      <Link
                        to="/outage"
                        className="text-primary-400 hover:text-primary-300 font-medium inline-block mt-2"
                      >
                        View outage details →
                      </Link>
                    </AlertDescription>
                  </Alert>
                )}

                {banners.length > 0 && (
                  <div className="flex flex-col gap-3">
                    <h2 className="text-lg font-semibold text-secondary-300">Active Notices</h2>
                    <div className="flex flex-col gap-2">
                      {banners.map((b) => (
                        <Alert key={b.id} variant={bannerVariant[b.severity] ?? 'info'}>
                          <AlertDescription>{b.message}</AlertDescription>
                        </Alert>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <h2 className="text-lg font-semibold text-secondary-300 mb-3">Services</h2>
                  {(services ?? []).length === 0 ? (
                    <Alert>
                      <AlertDescription>
                        No service rows are published in the status feed yet.
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <ul className="flex flex-col gap-2">
                      {(services ?? []).map((s) => (
                        <li
                          key={s.id}
                          className="flex items-center gap-3 rounded-lg px-4 py-2 bg-secondary-900/50 border border-secondary-700/30"
                        >
                          {s.enabled ? (
                            <CheckCircle className="size-4 text-success-400 shrink-0" aria-hidden />
                          ) : (
                            <AlertTriangle className="size-4 text-warning-400 shrink-0" aria-hidden />
                          )}
                          <span className="text-secondary-300 capitalize">{s.id}</span>
                          <Badge variant={s.enabled ? 'success' : 'warning'} className="ml-auto">
                            {s.enabled ? 'Operational' : 'Disabled'}
                          </Badge>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}

            <Separator className="my-8" />
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-primary-400 hover:text-primary-300 font-medium transition-colors"
            >
              Back to Home
            </Link>
          </CardContent>
        </Card>
      </Reveal>
      </div>
    </section>
  );
};

export default Status;
